"use client";
/**
 * The one app-wide handle on the Line. Opens the WebRTC line through our
 * door, feeds the fenced data-channel events into the machine, exposes the
 * typed door, and tears everything down when the line goes off, whoever
 * closed it: the person, the server's push, the channel, or a safety net.
 * The browser owns its own recovery on both sides: an open that never hears
 * session.started fails at 20 s, a close nobody answers ends at 15 s.
 * Mount-agnostic: any surface may call it; the store is the single truth,
 * the media handle included, never a component's, so a surface unmounting
 * can never strand a live mic.
 */
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  LINE_MAX_ROLLOVERS,
  destinationOfPath,
  type RouterEvent,
} from "@bash/shared";
import { SERVER_URL } from "@/lib/api/base";
import { getOptionalAccessToken } from "@/lib/api/client";
import { useTaskStore } from "@/stores/taskStore";
import { contextKey, contextOf } from "@/lib/composer/screen-context";
import { playStartCue, playStopCue, primeEarcons } from "@/lib/voice/earcons";
import {
  clearMicPref,
  primeMic,
  primeMicChoice,
  writeMicPref,
  type MicDeviceClass,
} from "@/lib/voice/mic";
import { judgeDeaf } from "@/lib/voice/deaf-watch";
import { idleMirror, type LineInput, type LineSnapshot } from "./lineMachine";
import {
  closeLine,
  liveContext,
  liveText,
  openLine,
  type LineHandle,
  type LiveServerEvent,
  type HealedRung,
} from "./liveClient";
import { tellLineMuted, useLiveStore } from "./liveStore";
import {
  applyVoiceQueryParam,
  getLiveVoice,
  migrateLocalVoice,
} from "./voicePreference";

/** The ball's first hover or focus settles the mic permission off the click path. */
export { primeMic };

/** The channel's session.started lifts connecting (the server's open push derives from the same event); past this the open has failed. ICE up to 10 s, then the door, then OpenAI. */
const OPEN_SAFETY_NET_MS = 20_000;
/** The server's closed push (or the channel close) finishes a close; past this the browser finishes it alone. */
const CLOSE_SAFETY_NET_MS = 15_000;
/** How long the face looks puzzled after a channel error. */
const RECONNECTING_FLASH_MS = 4000;

export interface LineControls {
  line: LineSnapshot;
  open(): Promise<void>;
  close(): Promise<void>;
  toggle(): Promise<void>;
  /** The text door while the line is open; null otherwise. The signal is the
   *  send's own, so a dismiss or a newer send aborts the parked fetch; `keys`
   *  are the send's request id and the id of the send it cancelled. */
  typed(
    input: string,
    signal?: AbortSignal,
    keys?: { idempotencyKey?: string; replaces?: string }
  ): AsyncGenerator<RouterEvent> | null;
  /** Read `remoteAnalyser` and `micAnalyser` off it only while the line is open. */
  handle: LineHandle | null;
}

let ticker: ReturnType<typeof setInterval> | null = null;
/** Counts opens; a door answering for an attempt that is no longer the latest is stale. */
let openAttempt = 0;
let openNet: ReturnType<typeof setTimeout> | null = null;
let safetyNet: ReturnType<typeof setTimeout> | null = null;
/**
 * THE DEAF CALL (2026-09-21, ruling R-D1). Silent redials spent on this CONVERSATION, not on this
 * line: the redial opens a new line, so a counter reset by the open would dial for ever. A
 * person-initiated open resets it; the redial's own open does not.
 */
let redials = 0;
/**
 * THE LINE STAYS ON (T8b): rollovers spent on this conversation, on the same rule as `redials`
 * (a person's open resets it, a redial's own open does not), and sent on every redial so the
 * server's next line knows its budget. Its own count, not the heal's: a rollover is not a deaf
 * call, and spending the heal's one redial on it would leave the rolled-over line with no heal.
 */
let rollovers = 0;
/** Mic swaps spent on this conversation's deaf verdicts: the ladder's first rung, once. */
let micSwaps = 0;
/** The class the swap put on the air, kept so a call HEARD after it can be remembered. */
let swappedTo: MicDeviceClass | null = null;
/** Which rung healed this conversation, for the close line. Null while nothing needed healing. */
let healedRung: HealedRung | null = null;
/** The give-up sentence is said once: a second deaf call is told about, never dialled again. */
let gaveUp = false;
/** The greeting mark is set once per line, on the server's first word that its greeting is done. */
let greetingMarked = false;
/** When the line we are watching went open, for the heal's own floor. */
let lineOpenedAt = 0;
let reconnectFlash: ReturnType<typeof setTimeout> | null = null;
/** The bearer the door used, kept for the one request that cannot wait for
 *  one: the close a dying document posts on pagehide. */
let pageHideToken: string | null = null;

const snapshot = (): LineSnapshot => useLiveStore.getState().line;
const dispatch = (i: LineInput): void => useLiveStore.getState().dispatch(i);
const handleOf = (): LineHandle | null => useLiveStore.getState().handle;

/** The path the browser is on. The open runs off a click, not a render, so it reads the document rather than the router. */
const browserPath = (): string =>
  typeof window !== "undefined" ? window.location.pathname : "";

/** What the door seeds the live model with: the zone and the view (spec 4.2), the view being the DESTINATION the path is on (spec 2026-09-17 5.3). */
function situation(): Record<string, unknown> {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* the fact drops; the server has a default */
  }
  const view = destinationOfPath(browserPath())?.id;
  return { ...(tz ? { tz } : {}), ...(view ? { view } : {}) };
}

/** The situation follows the person (spec 2026-09-17 I8): the view and the open task, sent while the line is open, once per change, this long after the last. */
export const CONTEXT_DEBOUNCE_MS = 300;
let contextTimer: ReturnType<typeof setTimeout> | null = null;
/** The situation the server already holds for the open line: the open's own stamp, then whatever the sender posted. A change that does not move it sends nothing. */
let lastContextKey = "";

function disarmContext(): void {
  if (contextTimer) clearTimeout(contextTimer);
  contextTimer = null;
}

/** The browser's old choice goes to the server once per page (voicePreference.ts). */
let voiceMigration: Promise<void> | null = null;
/** The audition switch: `?voice=<name>` on the page is stored first, then the
 *  stored choice rides the open; none means the server's (theirs, or the default). */
function liveVoice(): string | undefined {
  voiceMigration ??= migrateLocalVoice();
  applyVoiceQueryParam();
  return getLiveVoice() ?? undefined;
}

function disarmOpenNet(): void {
  if (openNet) clearTimeout(openNet);
  openNet = null;
}

/**
 * A reload or a hard navigation kills the mic and the peer with the document,
 * but nothing else would reach /close: the server would wait on OpenAI
 * noticing the peer is gone (up to the idle policy's ~70 s, billed). A
 * keepalive fetch survives unload and, unlike sendBeacon, carries the bearer;
 * a stale token just makes the server answer 401 and OpenAI stays the floor.
 */
function onPageHide(): void {
  const id = handleOf()?.sessionId;
  if (!id || !pageHideToken) return;
  void fetch(
    `${SERVER_URL}/api/composer/live/${encodeURIComponent(id)}/close`,
    {
      method: "POST",
      keepalive: true,
      headers: { authorization: `Bearer ${pageHideToken}` },
    }
  ).catch(() => undefined);
}

/** Media off, timers off. Idempotent; the store's transition is the caller's. */
function releaseLine(): void {
  const handle = handleOf();
  if (handle) {
    handle.teardown();
    // The mute goes with the line it belonged to: the next call is a fresh
    // mic, and a level left on would take it off the air with nothing but
    // the tray's button to say why. The silent-mic trouble goes the same way,
    // and so does the count of files waiting on it (attach on the call).
    useLiveStore.setState({
      handle: null,
      muted: false,
      micTrouble: null,
      staged: 0,
    });
  }
  window.removeEventListener("pagehide", onPageHide);
  window.removeEventListener("pageshow", onPageShow);
  pageHideToken = null;
  if (ticker) clearInterval(ticker);
  ticker = null;
  disarmOpenNet();
  if (safetyNet) clearTimeout(safetyNet);
  safetyNet = null;
  if (reconnectFlash) clearTimeout(reconnectFlash);
  reconnectFlash = null;
}

// Level-triggered against the store: the clock runs exactly while the line is
// open, and the media goes the moment the line goes off, whichever path took
// it there (the channel's session.closed, the server's push through the
// socket handler, the safety net, a failed open).
useLiveStore.subscribe((s, prev) => {
  if (s.line.phase === "open" && !ticker) {
    lineOpenedAt = Date.now();
    greetingMarked = false;
    ticker = setInterval(() => {
      const at = Date.now();
      dispatch({ type: "tick", at });
      runDeafWatch(at);
      // The idle mirror rides the same tick (spec §7): the verdict lands
      // once, on; only the person speaking takes it off (the machine's you
      // input), never a later tick, so the question stays while Bash asks it.
      const line = snapshot();
      if (!line.idleAsk && idleMirror(line, at))
        dispatch({ type: "idle_ask", on: true });
    }, 1000);
  } else if (s.line.phase !== "open" && ticker) {
    clearInterval(ticker);
    ticker = null;
  }
  // Leaving connecting by any door (started, a close, a failure) ends the open net.
  if (prev.line.phase === "connecting" && s.line.phase !== "connecting")
    disarmOpenNet();
  if (s.line.phase === "off" && prev.line.phase !== "off") {
    releaseLine();
    // The mute as it stood on the line that ended: releaseLine clears it.
    if (s.rollingOver) rollOver(s.muted);
  }
});

/**
 * THE LINE STAYS ON (T8b, spec 2026-09-25 §2.2). The server ended this line at its cap with reason
 * `rollover`, so the conversation goes on at once on a fresh line: silent, seeded with the recent
 * exchanges as every redial is, and in this same beat, so the ball never rests on the closed note.
 * The flag and the count are spent BEFORE the dial: a dial that fails (the network, credits) lands
 * on today's failed state and nothing dials again. It never dials past the budget, and never for a
 * muted page (a fresh line is a live mic; the server's mute tracker is a hint, this is the gate).
 * A rollover it refuses is today's close, with the stop cue it owns: never a silent dead line.
 */
function rollOver(muted: boolean): void {
  useLiveStore.setState({ rollingOver: false });
  if (muted || rollovers >= LINE_MAX_ROLLOVERS) {
    playStopCue();
    return;
  }
  rollovers += 1;
  void open({ redial: true });
}

/**
 * THE DEAF CALL HEALS ITSELF (R-D1), once a second while the line is open. The rule is
 * lib/voice/deaf-watch; this only gathers the facts and acts on the verdict. Both halves are
 * needed: the server's (its greeting is done, it has heard nothing) and the browser's (he spoke,
 * he stopped, and the sender carried it). Off behind the door's flag, and silent on any doubt.
 */
function runDeafWatch(at: number): void {
  const s = useLiveStore.getState();
  const handle = s.handle;
  if (!handle?.deafHeal || s.line.phase !== "open" || gaveUp) return;
  // HEARD after a rung: name the rung for the close line, and when it was the
  // mic, remember that class for this machine's next call. Only a swap teaches
  // us something: a redial proves nothing about which input to open on.
  if (s.heard && healedRung === null && (swappedTo || redials > 0)) {
    healedRung = swappedTo ? "mic" : "redial";
    if (swappedTo) writeMicPref(swappedTo);
    swappedTo = null;
  }
  // The greeting's end is the server's word, and the meter counts from there:
  // words he said OVER the greeting are not an unanswered turn.
  if (s.awaitingVoice && !greetingMarked) {
    greetingMarked = true;
    handle.markGreetingEnd();
    return;
  }
  const heardSelf = handle.speech();
  const verdict = judgeDeaf({
    openMs: at - lineOpenedAt,
    awaitingVoice: s.awaitingVoice,
    // Before the mark there is no window to measure, and absent never dials.
    voicedMs: greetingMarked ? heardSelf.voicedMs : null,
    quietMs: heardSelf.quietMs,
    sentLevel: heardSelf.sentLevel,
    heard: s.heard,
    muted: s.muted,
    // A delegation this line has relayed. It is only ever set after we were
    // heard, so it can make the watch more careful and never less.
    working: s.turnDelegationId !== null,
    micSwaps,
    redials,
  });
  if (verdict === "swap") {
    micSwaps += 1;
    // Swapping away from whatever is on the air: if that was the remembered
    // class, it has just been proved wrong on this machine.
    clearMicPref();
    void swapMic(handle);
  } else if (verdict === "redial") {
    redials += 1;
    void redial();
  } else if (verdict === "give-up") {
    gaveUp = true;
    // Neither rung took, so the class the swap tried is not the one to keep.
    clearMicPref();
    handleOf()?.flushMicStats?.("no");
    useLiveStore.getState().setMicTrouble("deaf");
  }
}

/**
 * THE LADDER'S FIRST RUNG: another input on the same call. It costs him no greeting and no
 * pause, and on his calls it is the likeliest fix, because every deaf one of them is on the
 * built-in mic while he is wearing earbuds. A swap that finds nothing to try spends its rung
 * anyway: the next verdict is then the dial, rather than this one over and over.
 */
async function swapMic(handle: LineHandle): Promise<void> {
  swappedTo = await handle.swapMic().catch(() => null);
}

/**
 * One silent redial: the evidence first (while the server still has a line to log under), then
 * the same teardown the safety nets take, then a fresh line that opens with no greeting. Same
 * thread, same screen; the ball wears its ordinary connecting state and nothing else is said.
 */
async function redial(): Promise<void> {
  const id = handleOf()?.sessionId;
  handleOf()?.flushMicStats?.();
  dispatch(closedNow("deaf_redial", snapshot().seconds));
  releaseLine();
  if (id) await closeLine(id).catch(() => undefined);
  await open({ redial: true });
}

const closedNow = (reason: string, seconds: number): LineInput => ({
  type: "server_state",
  state: "closed",
  reason,
  seconds,
  at: Date.now(),
});

/** A page restored from the back/forward cache comes back with the store
 *  still saying open, against a line the pagehide close (or OpenAI) ended:
 *  the browser finishes it the way the close net does. */
function onPageShow(e: Event): void {
  if (!(e as PageTransitionEvent).persisted) return;
  const s = snapshot();
  if (s.phase === "off") return;
  dispatch(closedNow("connection_lost", s.seconds));
  releaseLine();
}

function onEvent(e: LiveServerEvent): void {
  switch (e.type) {
    case "session.started": {
      const fromEvent = (e.session as { id?: string } | undefined)?.id;
      // The chime and the eyes opening are one moment (spec §4, §12.4): the
      // cue plays when the model is listening, never when the peer's answer
      // lands, and never for a started the machine refuses (a close beat it,
      // or the line is already open). The machine's answer is read, not
      // restated: the cue is the connecting to open transition itself.
      const before = snapshot().phase;
      dispatch({
        type: "started",
        sessionId: handleOf()?.sessionId ?? String(fromEvent ?? ""),
        at: Date.now(),
      });
      if (before === "connecting" && snapshot().phase === "open")
        playStartCue();
      break;
    }
    case "session.delegation.created":
      dispatch({ type: "delegation" });
      break;
    case "session.closed": {
      const usage = e.usage as { seconds?: number } | undefined;
      // A line rolling over owns its cue (T8b, rollOver): the new line's start,
      // or the stop cue of a rollover it refused. Read before the close lands.
      const rolling = useLiveStore.getState().rollingOver;
      dispatch(closedNow(String(e.reason ?? ""), Number(usage?.seconds ?? 0)));
      if (!rolling) playStopCue();
      break;
    }
    case "error":
      dispatch({ type: "reconnecting", on: true });
      if (reconnectFlash) clearTimeout(reconnectFlash);
      reconnectFlash = setTimeout(() => {
        reconnectFlash = null;
        dispatch({ type: "reconnecting", on: false });
      }, RECONNECTING_FLASH_MS);
      break;
    default:
      break;
  }
}

function onChannelClosed(): void {
  const s = snapshot();
  if (s.phase !== "off") dispatch(closedNow("connection_lost", s.seconds));
  releaseLine();
}

async function open(opts?: { redial?: boolean }): Promise<void> {
  if (snapshot().phase !== "off") return;
  // A person opening the line starts the heal's count over; the redial's own
  // open does not, or a deaf conversation would dial for ever.
  if (!opts?.redial) {
    redials = 0;
    rollovers = 0;
    micSwaps = 0;
    swappedTo = null;
    healedRung = null;
    gaveUp = false;
  }
  // Nothing may be left behind from before: the new handle never overwrites one.
  releaseLine();
  primeEarcons();
  dispatch({ type: "open", at: Date.now() });
  // The last line's receipt survives its close (a routed card's linger
  // completes and stays clickable), never a new open: the fresh ball must
  // not wear "Out of credits" until Bash's first delegation resets the turn.
  useLiveStore.setState({
    turn: [],
    turnDelegationId: null,
    muted: false,
    micTrouble: null,
    staged: 0,
  });
  // A fresh line starts with the open's own stamp (situation() below): the
  // sender posts the first change AFTER it, never a repeat of it. The open
  // stamps no task, so a line opened on a task page learns that one from the
  // first send.
  lastContextKey = contextKey(contextOf(browserPath(), null));
  // Armed before the door is called; only session.started (or a close, or a
  // failure) disarms it. Past it the line is dead however far it got.
  openNet = setTimeout(() => {
    openNet = null;
    // Read before the failure lands: the store's off transition releases
    // the handle. A door that answered inside the window created a metered
    // line whose session.started never reached the channel; the server's
    // sideband saw it start, so it must hear the ending from us, or it
    // meters until OpenAI notices the peer is gone.
    const id = handleOf()?.sessionId;
    if (snapshot().phase === "connecting")
      dispatch({
        type: "failed",
        message: "line_failed:timeout",
        at: Date.now(),
      });
    releaseLine();
    if (id) void closeLine(id).catch(() => undefined);
  }, OPEN_SAFETY_NET_MS);
  // The door has no timeout past ICE, so it may answer long after the net
  // has fired and the person has opened again. A phase check alone cannot
  // tell our late answer from the retry's live attempt; the token can.
  const attempt = ++openAttempt;
  // Read alongside the door (the door reads the same token itself), never on
  // the click path: it is only for the pagehide close.
  const tokenForPageHide = getOptionalAccessToken().catch(() => null);
  let opened: LineHandle;
  try {
    opened = await openLine({
      voice: liveVoice(),
      context: situation(),
      ...(opts?.redial ? { redial: true } : {}),
      ...(rollovers ? { rollovers } : {}),
      onEvent,
      onChannelClosed,
      // The silent-mic watchdog's word: the stage's one line, and the server
      // told on the mute's rail (the store does both, once per change).
      onMicSilent: (silent) =>
        useLiveStore.getState().setMicTrouble(silent ? "silent" : null),
      // The evidence line: this call's one picture of its own mic and uplink,
      // on the same rail, with the mute as it stands.
      onMicStats: (stats) =>
        tellLineMuted(useLiveStore.getState().muted, undefined, stats),
    });
  } catch (err) {
    if (attempt !== openAttempt) return; // a retry's line, not ours to touch
    const phase = snapshot().phase;
    if (phase === "connecting") {
      dispatch({
        type: "failed",
        message: err instanceof Error ? err.message : "line_failed",
        at: Date.now(),
      });
    } else if (phase === "closing") {
      // The person already closed; a door that never opened has nothing to
      // wait for, so the line ends now rather than at the close net.
      dispatch(closedNow("connection_lost", snapshot().seconds));
    }
    // off: the open net (or the channel) already settled it; nothing to add.
    return;
  }
  // A handle for a line that is no longer ours (a retry took over) or no
  // longer alive (the net, or the channel dying, ended it) is torn down,
  // never kept, and its server line is closed: the door already created it,
  // metered, and nothing else would end it before the idle policy.
  if (attempt !== openAttempt || snapshot().phase === "off") {
    opened.teardown();
    void closeLine(opened.sessionId).catch(() => undefined);
    return;
  }
  useLiveStore.setState({ handle: opened });
  // A fresh line is unmuted, and the server is told so once here rather than left to infer it:
  // its idle policy holds while the mic is off the air, and nothing else would ever say it is on.
  tellLineMuted(false);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  // Never awaited on the open path: a slow token read must not hold the
  // handle outside the store (where the net could not release it) nor delay
  // the store's transition. Until it lands, a pagehide simply has no close
  // to post.
  void tokenForPageHide.then((t) => {
    if (handleOf() === opened) pageHideToken = t;
  });
  // The person closed while we were still connecting: the door now has an
  // id to close, and there is nothing to celebrate.
  if (snapshot().phase === "closing") {
    void closeLine(opened.sessionId).catch(() => undefined);
    return;
  }
}

async function close(): Promise<void> {
  const s = snapshot();
  if (s.phase === "off") return;
  // His hang-up is never rolled over, even with the server's rollover on its way (T8b).
  useLiveStore.setState({ rollingOver: false });
  dispatch({ type: "close" });
  // Armed BEFORE the door is asked: a close request that never settles must
  // still end the line, or the mic stays live behind a phase stuck at closing.
  if (safetyNet) clearTimeout(safetyNet);
  safetyNet = setTimeout(() => {
    safetyNet = null;
    if (snapshot().phase === "closing")
      dispatch(closedNow("connection_lost", snapshot().seconds));
    releaseLine();
  }, CLOSE_SAFETY_NET_MS);
  const id = handleOf()?.sessionId ?? s.sessionId;
  // A call ended before its ten seconds leaves its evidence now, BEFORE the
  // door is told: after it the server has no line to log under. Never awaited.
  handleOf()?.flushMicStats?.(healedRung ?? undefined);
  // No token means the door has nothing to close; the browser still finishes.
  if (id) await closeLine(id).catch(() => undefined);
}

async function toggle(): Promise<void> {
  if (snapshot().phase === "off") await open();
  else await close();
}

function typed(
  input: string,
  signal?: AbortSignal,
  keys?: { idempotencyKey?: string; replaces?: string }
): AsyncGenerator<RouterEvent> | null {
  const s = snapshot();
  if (s.phase !== "open" || !s.sessionId) return null;
  return liveText(s.sessionId, input, {
    signal,
    idempotencyKey: keys?.idempotencyKey,
    replaces: keys?.replaces,
  });
}

/** `enabled` false (the surface's flag is off) primes nothing: hooks cannot
 *  be called conditionally, and a flag-off Dock must have no side effect. */
export function useLine(enabled = true): LineControls {
  const line = useLiveStore((s) => s.line);
  const handle = useLiveStore((s) => s.handle);
  const pathname = usePathname();
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  const openId = line.phase === "open" ? line.sessionId : null;
  useEffect(() => {
    if (enabled) primeMicChoice();
  }, [enabled]);
  // The situation follows the person (I8): debounced, one timer app-wide (every
  // surface calls this hook), keyed so an unchanged context arms nothing. A
  // line that is not open disarms it: a debounce still in flight would post to
  // a session that is gone. A disabled consumer leaves before the disarm: a
  // flag-off surface mounted beside an enabled one must not cancel its post.
  useEffect(() => {
    if (!enabled) return;
    disarmContext();
    if (!openId || !pathname) return;
    const next = contextOf(pathname, selectedTaskId);
    const key = contextKey(next);
    if (key === lastContextKey) return;
    contextTimer = setTimeout(() => {
      contextTimer = null;
      lastContextKey = key;
      void liveContext(openId, next).catch(() => undefined);
    }, CONTEXT_DEBOUNCE_MS);
  }, [enabled, openId, pathname, selectedTaskId]);
  return { line, open, close, toggle, typed, handle };
}
